import * as React from 'react';
import { connect } from 'react-redux';

import DeliverableMediaUploadPlaceholder from '../../media/parts/placeholder/DeliverableMediaUploadPlaceholder';
import ImageUploader, {
  MEDIA_TYPES_IMAGES,
  MEDIA_TYPES_IMAGES_OR_VIDEOS_OR_PDF
} from 'components/ImageUploader';

import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_CONTENT,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES } from 'utils/deliverables';

import { uploadDeliverableAttachments } from '../../../reducers/deliverableMediaAttachments';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentFormat,
  DeliverableAttachmentType,
  DeliverableTempAttachment
} from 'types';
import type UploaderUpdateHandler from './UploaderUpdateHandler.type';

import './deliverable-attachment-uploader.scss';

type OwnProps = {
  size?: string,
  deliverable: Deliverable,
  attachmentType: DeliverableAttachmentType,
  onUpdate: UploaderUpdateHandler,
  isReplacement?: boolean,
  forwardedRef?: React.ForwardedRef<HTMLInputElement>
};

type StateProps = {
  uploadProgressFiles: Record<string, number>
}

type DispatchProps = {
  dispatchUploadDeliverableAttachment: (
    files: Array<File>,
    deliverable: Deliverable,
    attachmentType: DeliverableAttachmentType,
    uploadId: string
  ) => Promise<Array<DeliverableAttachment>>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isDragAndDropping: boolean,
  uploadingAttachments: Array<DeliverableTempAttachment>,
  error: string
};

const MEDIA_TYPES = {
  [DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS]: MEDIA_TYPES_IMAGES,
  [DELIVERABLE_ATTACHMENT_TYPE_HIGHRES]: MEDIA_TYPES_IMAGES_OR_VIDEOS_OR_PDF,
  [DELIVERABLE_ATTACHMENT_TYPE_CONTENT]: MEDIA_TYPES_IMAGES_OR_VIDEOS_OR_PDF
};

class DeliverableAttachmentUploader extends React.PureComponent<Props, State> {
  state: State = {
    isDragAndDropping: false,
    uploadingAttachments: [],
    error: ''
  };

  handleUploadFiles = (files: FileList) => {
    const { attachmentType, deliverable, dispatchUploadDeliverableAttachment, onUpdate } = this.props;

    const fileList = [...files].filter(file => ['image', 'video', 'pdf'].some(type => file.type.indexOf(type) >= 0));
    const uploadId = Math.random();

    const tempAttachments = fileList.map(file => ({
      uploadId,
      id: `${uploadId}-${file.name}`,
      fileName: file.name,
      meta: {
        size: file.size,
        type: attachmentType
      },
      url: '',
      assetType: file.type as DeliverableAttachmentFormat
    }));

    this.setState((prevState) => ({
      ...prevState,
      uploadingAttachments: [...prevState.uploadingAttachments, ...tempAttachments],
      error: ''
    }));

    dispatchUploadDeliverableAttachment(fileList, deliverable, attachmentType, String(uploadId))
      .then((attachements) => {
        const { uploadingAttachments } = this.state;
        const attachmentsLeftToBeUploaded = uploadingAttachments.filter(uploadingAttachment => uploadingAttachment.uploadId !== uploadId);
        const attachmentsWithOutUploadError = attachements.filter(attachment => !('errorMessage' in attachment));
        const attachmentsWithUploadError = attachements.filter(attachment => 'errorMessage' in attachment);
        let error = '';

        if (attachmentsWithOutUploadError.length !== attachements.length && attachmentsWithUploadError[0].errorMessage) {
          error = attachmentsWithUploadError[0].errorMessage;
        }

        this.setState((prevState) => ({
          ...prevState,
          uploadingAttachments: attachmentsLeftToBeUploaded,
          error
        }));

        if (attachmentsWithOutUploadError.length > 0) {
          onUpdate(attachmentsWithOutUploadError);
        }
      });
  };

  handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: true
    });
  };

  handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: false
    });
  };

  handleDropFinished = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: false
    });

    this.handleUploadFiles(event.dataTransfer.files);
  };

  render(): React.ReactNode {
    const { attachmentType, size, uploadProgressFiles, isReplacement, forwardedRef } = this.props;
    const { uploadingAttachments, error } = this.state;
    const { isDragAndDropping } = this.state;
    const placeholderSize = size || 'normal';

    return (
      <React.Fragment>
        {
          uploadingAttachments.map(attachment => (
            <div
              key={attachment.id}
            >
              <DeliverableMediaUploadPlaceholder
                progress={uploadProgressFiles[`${attachment.uploadId}${attachment.fileName}`]}
                size={placeholderSize}
                mode="uploading"
              />
            </div>
          ))
        }
        {
          (!isReplacement || uploadingAttachments.length == 0) &&
            <div
              onDragEnter={this.handleDragEnter}
              onDragLeave={this.handleDragLeave}
              onDragOver={this.handleDragOver}
              onDrop={this.handleDropFinished}
            >
              <ImageUploader
                key={`uploader-${uploadingAttachments.length > 0 ? 'uploading' : 'ready'}`} // force to rerender to upload the same file again in Chrome COLLAB-3929
                multiple
                mediaTypes={MEDIA_TYPES[attachmentType]}
                onUpload={this.handleUploadFiles}
                forwardedRef={forwardedRef}
              >
                <DeliverableMediaUploadPlaceholder
                  size={placeholderSize}
                  mode={isDragAndDropping ? 'drop' : 'create'}
                />
              </ImageUploader>
            </div>
        }
        {
          error &&
            <div className="deliverable-attachment-uploader__error">
              {error}
            </div>
        }
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ uploadProgressReducer }): StateProps => ({
  uploadProgressFiles: uploadProgressReducer.files
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUploadDeliverableAttachment: (files: Array<File>, deliverable: Deliverable, attachmentType: DeliverableAttachmentType, uploadId: string) =>
    dispatch(uploadDeliverableAttachments(files, deliverable, attachmentType, uploadId))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableAttachmentUploader);
